import PollDesign from '@components/common/PollDesign'
import PollNavButton from '@components/common/PollNavButton'
import React from 'react'
import ViewNavigation from '@components/common/ViewNavigation'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import TodayIcon from '@mui/icons-material/Today'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import { GlassOutlineButton } from '@components/common/Glass'
import {
  altColor,
  altColorDarker,
  altColorDarkText,
  altColorLighter,
  altColorLightText,
  mainColor,
  mainColorDarker,
  mainColorDarkText,
  mainColorLighter,
  mainColorMediumText,
  shadow1,
} from 'src/ui/theme/tokens'
import MultiPlayView from './game/MultiPlayView'
import HostPlayView from './game/HostPlayView'
import SoloPlayView from './game/SoloPlayView'
import {
  DailyListResult,
  DailyProgressState,
  OnlineRematchState,
  OnlineVoteState,
  Player,
} from 'src/pages/Dashboard'
import { List } from 'src/api/types/lists'
import { Item } from '@components/common/ItemGuessBox'
import { AlertInput } from '@components/common/Alert'
import { UserState } from 'src/app/AppContext'
import { GameSounds } from 'src/ui/sound/types'
import { UserSoloStats } from 'src/api/types/users'
import Typography from '@mui/material/Typography'
export type DashboardGameViewsProps = {
  hidden?: boolean
  gameState: string
  mode: 'single' | 'multi' | 'host' | undefined
  multiMode: string
  fullScreen: boolean
  selectedList: number | undefined
  activePollId?: number
  hostPlayMode: string
  orderedListItems: Item[]
  listItems: Item[]
  setListItems: (items: Item[]) => void
  myPlayer?: Player
  players: Player[]
  authoritativeOnlineAction?: {
    nonce: number
    selectedIndex: number
    result?: 'hit' | 'miss'
    actingTeam?: number
    teamTurn?: number
    teamOneScore?: number
    teamTwoScore?: number
  }
  onlineVoteState?: OnlineVoteState
  onlineMatchRated?: boolean
  onlineMatchTrophyDelta?: number
  multiplayerXpAwarded?: number
  onlineRematchState?: OnlineRematchState
  isOnlineHost?: boolean
  hostId?: string
  connectionId?: string
  ws?: WebSocket
  gameCode?: string
  handleGameOver: () => void
  handleNewGame: (options?: { silentPollStart?: boolean; forceRandomOnlineLobby?: boolean }) => void
  handleHostPlayMode: (mode: string) => void
  dailyList?: List
  dailyResult?: DailyListResult | undefined
  dailyProgress?: DailyProgressState | undefined
  loading: boolean
  listRated?: number
  appUserId?: number
  user?: UserState
  doCreate: () => Promise<number | undefined>
  topic: string
  sounds: GameSounds
  handleEndGame: () => void
  handleRestartGame: () => void
  handlePlayAgainMatchmaking?: () => void
  handleLeaveOnlineLobby?: () => void
  handleLeaveMatchmakingSession?: () => void
  handleCreateList: () => void
  handleRateList: (rating: number) => void
  setRateAlert: (value: AlertInput) => void
  historyPush: (path: string) => void
  soloGameKey: number
  handlePreScreen: () => void
  setGameState: (value: string) => void
  setDailyResult: (value: DailyListResult | undefined) => void
  onDailyProgressChange: (value?: DailyProgressState) => void
  dailyPollContext?: 'today' | 'previous'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlePlayedList: (listId: number, data: any) => void
  onHasGuessChange: (hasGuess: boolean) => void
  soloStats?: UserSoloStats
  soloXpAwarded?: number
  onSoloStatsUpdate: (stats?: UserSoloStats) => void
  onSoloXpAwarded: (xp?: number) => void
  exitLabel: string
  onExit: () => void
  newListLabel: string
  newListDisabled?: boolean
  onNewList: () => void
}

const DashboardGameViews: React.FC<DashboardGameViewsProps> = ({
  hidden = false,
  gameState,
  mode,
  multiMode,
  fullScreen,
  selectedList,
  activePollId,
  hostPlayMode,
  orderedListItems,
  listItems,
  setListItems,
  myPlayer,
  players,
  authoritativeOnlineAction,
  onlineVoteState,
  onlineMatchRated,
  onlineMatchTrophyDelta,
  multiplayerXpAwarded,
  onlineRematchState,
  isOnlineHost,
  hostId,
  connectionId,
  ws,
  gameCode,
  handleGameOver,
  handleNewGame,
  handleHostPlayMode,
  dailyList,
  dailyResult,
  dailyProgress,
  loading,
  listRated,
  appUserId,
  user,
  doCreate,
  topic,
  sounds,
  handleEndGame,
  handleRestartGame,
  handlePlayAgainMatchmaking,
  handleLeaveOnlineLobby,
  handleLeaveMatchmakingSession,
  handleCreateList,
  handleRateList,
  setRateAlert,
  historyPush,
  soloGameKey,
  handlePreScreen,
  setGameState,
  setDailyResult,
  onDailyProgressChange,
  dailyPollContext,
  handlePlayedList,
  onHasGuessChange,
  soloStats,
  soloXpAwarded,
  onSoloStatsUpdate,
  onSoloXpAwarded,
  exitLabel,
  onExit,
  newListLabel,
  newListDisabled = false,
  onNewList,
}) => {
  const RefreshButton = activePollId ? PollNavButton : GlassOutlineButton
  return (
    <PollDesign active={!!activePollId} sx={{ display: hidden ? 'none' : undefined }}>
      {gameState !== 'pre' ? (
        <ViewNavigation>
          <GlassOutlineButton
            startIcon={<ArrowBackRoundedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
            onClick={onExit}
            sx={{
              pl: 2,
              pr: 3,
              py: 0.75,
              color: '#60768b',
              fontWeight: 600,
              boxShadow: shadow1,
            }}
          >
            {exitLabel}
          </GlassOutlineButton>
          <RefreshButton
            variant="outlined"
            onClick={(event) => {
              ;(event.currentTarget as HTMLElement).blur()
              onNewList()
            }}
            onTouchEnd={(event) => {
              ;(event.currentTarget as HTMLElement).blur()
            }}
            disabled={newListDisabled}
            startIcon={
              newListLabel === 'Daily Challenge' ? (
                <TodayIcon fontSize="inherit" color="disabled" />
              ) : newListLabel === 'New List' || newListLabel === 'New Poll' ? (
                <AutorenewRoundedIcon fontSize="inherit" color="disabled" />
              ) : newListLabel === 'Unranked' ? null : null
            }
            endIcon={newListLabel.includes('Ranked ') ? <EmojiEventsRoundedIcon /> : null}
            sx={activePollId ? undefined : {
              py: 0.75,
              px: 2,
              minHeight: 38,
              minWidth: { xs: 100, sm: 140 },
              color: '#096586',
              background: '#e1f5fb',
              boxShadow: '0 2px 0 #d6e4ef',
              '&:hover': { background: '#e1f5fb' },
            }}
          >
            {newListLabel}
          </RefreshButton>
        </ViewNavigation>
      ) : null}
      {mode === 'single' ? (
        <SoloPlayView
          gameState={gameState}
          mode={mode}
          multiMode={multiMode}
          fullScreen={fullScreen}
          listItems={listItems}
          soloGameKey={soloGameKey}
          topic={topic}
          loading={loading}
          activePollId={activePollId}
          sounds={sounds}
          handleEndGame={handleEndGame}
          handleNewGame={handleNewGame}
          handlePreScreen={handlePreScreen}
          setGameState={setGameState}
          selectedList={selectedList}
          dailyList={dailyList}
          dailyResult={dailyResult}
          dailyProgress={dailyProgress}
          setDailyResult={setDailyResult}
          onDailyProgressChange={onDailyProgressChange}
          dailyPollContext={dailyPollContext}
          handlePlayedList={handlePlayedList}
          handleRestartGame={handleRestartGame}
          handleCreateList={handleCreateList}
          handleRateList={handleRateList}
          setRateAlert={setRateAlert}
          historyPush={historyPush}
          listRated={listRated}
          appUserId={appUserId}
          user={user}
          doCreate={doCreate}
          onHasGuessChange={onHasGuessChange}
          soloStats={soloStats}
          soloXpAwarded={soloXpAwarded}
          onSoloStatsUpdate={onSoloStatsUpdate}
          onSoloXpAwarded={onSoloXpAwarded}
        />
      ) : mode === 'host' ? (
        <HostPlayView
          gameState={gameState}
          mode={mode}
          multiMode={multiMode}
          fullScreen={fullScreen}
          selectedList={selectedList}
          topic={topic}
          loading={loading}
          activePollId={activePollId}
          contentType={activePollId ? 'poll' : 'trivia'}
          hostPlayMode={hostPlayMode}
          orderedListItems={orderedListItems}
          listItems={listItems}
          setListItems={setListItems}
          myPlayer={myPlayer}
          players={players}
          authoritativeOnlineAction={authoritativeOnlineAction}
          onlineVoteState={onlineVoteState}
          onlineMatchRated={onlineMatchRated}
          onlineMatchTrophyDelta={onlineMatchTrophyDelta}
          multiplayerXpAwarded={multiplayerXpAwarded}
          onlineRematchState={onlineRematchState}
          isOnlineHost={isOnlineHost}
          hostId={hostId}
          connectionId={connectionId}
          ws={ws}
          gameCode={gameCode}
          sounds={sounds}
          onShuffleComplete={setListItems}
          onGameOver={handleGameOver}
          handleNewGame={handleNewGame}
          handleHostPlayMode={handleHostPlayMode}
          handleEndGame={handleEndGame}
          handleRestartGame={handleRestartGame}
          handlePlayAgainMatchmaking={handlePlayAgainMatchmaking}
          handleLeaveOnlineLobby={handleLeaveOnlineLobby}
          handleLeaveMatchmakingSession={handleLeaveMatchmakingSession}
          handleCreateList={handleCreateList}
          handleRateList={handleRateList}
          setRateAlert={setRateAlert}
          historyPush={historyPush}
          listRated={listRated}
          appUserId={appUserId}
          user={user}
          doCreate={doCreate}
          dailyList={dailyList}
          dailyResult={dailyResult}
        />
      ) : (
        <MultiPlayView
          gameState={gameState}
          mode={mode}
          multiMode={multiMode}
          fullScreen={fullScreen}
          selectedList={selectedList}
          topic={topic}
          loading={loading}
          activePollId={activePollId}
          contentType={activePollId ? 'poll' : 'trivia'}
          hostPlayMode={hostPlayMode}
          orderedListItems={orderedListItems}
          listItems={listItems}
          setListItems={setListItems}
          myPlayer={myPlayer}
          players={players}
          authoritativeOnlineAction={authoritativeOnlineAction}
          onlineVoteState={onlineVoteState}
          onlineMatchRated={onlineMatchRated}
          onlineMatchTrophyDelta={onlineMatchTrophyDelta}
          multiplayerXpAwarded={multiplayerXpAwarded}
          onlineRematchState={onlineRematchState}
          isOnlineHost={isOnlineHost}
          hostId={hostId}
          connectionId={connectionId}
          ws={ws}
          gameCode={gameCode}
          sounds={sounds}
          onShuffleComplete={setListItems}
          onGameOver={handleGameOver}
          handleNewGame={handleNewGame}
          handleHostPlayMode={handleHostPlayMode}
          handleEndGame={handleEndGame}
          handleRestartGame={handleRestartGame}
          handlePlayAgainMatchmaking={handlePlayAgainMatchmaking}
          handleLeaveOnlineLobby={handleLeaveOnlineLobby}
          handleLeaveMatchmakingSession={handleLeaveMatchmakingSession}
          handleCreateList={handleCreateList}
          handleRateList={handleRateList}
          setRateAlert={setRateAlert}
          historyPush={historyPush}
          listRated={listRated}
          appUserId={appUserId}
          user={user}
          doCreate={doCreate}
          dailyList={dailyList}
          dailyResult={dailyResult}
        />
      )}
    </PollDesign>
  )
}

export default DashboardGameViews
